import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/** Every backend response carries these; mutation routes carry nothing the tile reads beyond them. */
export interface BaseResponse {
  status: number;
  message: string;
}

export interface DonorAtlasStatusResponse extends BaseResponse {
  authorized: boolean;
  configured: boolean;
  processing: boolean;
  profile_url?: string | null;
  match_prob?: number;
  other_matches?: { [key: string]: number };
  last_synced_at?: number;
  last_synced_by?: string | null;
  // Which op last stamped the sync ('sync' | 'resync'); null/absent on
  // rows stamped before the column existed — render as a plain sync.
  last_synced_op?: string | null;
  // Progress of the active backend job. Optional and nullable: without
  // them the tile shows the processing pill with no bar.
  progress?: number | null;
  total?: number | null;
  // Constituent display name, present only in the match-panel states
  // (not synced / no match, not processing).
  constituent_name?: string | null;
  // "Individual" / "Organization", alongside constituent_name in the
  // not-synced state. Organizations render a placeholder card.
  constituent_type?: string | null;
  // Strong-name-match DonorAtlas entity for an org constituent.
  org_entity_name?: string | null;
  org_entity_url?: string | null;
  // "Nonprofit" / "School" / "Company" when matched.
  org_entity_type?: string | null;
  // Entity logo (website favicon) for the org card.
  org_entity_logo_url?: string | null;
  // Which mutation the active job runs (integration_sync / _resync /
  // _unsync), so the tile can say "Unsyncing…" instead of "Syncing…".
  active_job_type?: string | null;
  // What this user may do through the integration ('sync' | 'unsync' |
  // 'bulk'), set by their DonorAtlas team admin; viewing always comes
  // with the connection, so [] is view-only. Absent on older backends —
  // treat as everything. The backend enforces the same set.
  permissions?: string[];
  // The team's pooled SKY daily quota is spent right now. The tile
  // replaces its mutation buttons with quota_message while it stands.
  // Absent on older backends — buttons stay live.
  sky_quota_exhausted?: boolean;
  quota_message?: string | null;
}

/** The status response minus its HTTP status code: what the tile keeps and caches. */
export type DonorAtlasSummary = Omit<DonorAtlasStatusResponse, 'status'>;

export interface DonorSearchResult {
  donor_id: string;
  name: string;
  location?: string | null;
  photo_url?: string | null;
  profile_url?: string | null;
}

export interface DonorSearchResponse extends BaseResponse {
  results: DonorSearchResult[];
}

export interface EmbedDataResponse extends BaseResponse {
  embed_url: string;
  donor?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class DonorAtlasService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private userIdentityToken: string | null = null;

  /**
   * Set the User Identity Token from Blackbaud for authenticated requests.
   */
  public setUserIdentityToken(token: string): void {
    this.userIdentityToken = token;
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (this.userIdentityToken) {
      headers = headers.set('X-Blackbaud-User-Identity-Token', this.userIdentityToken);
    }

    return headers;
  }

  /** Dev-only console log of a failed request, then rethrow for the caller's own handling. */
  private logAndRethrow<T>(label: string): MonoTypeOperatorFunction<T> {
    return catchError((error) => {
      if (!environment.production) {
        console.error(`${label}:`, error);
      }
      return throwError(() => error);
    });
  }

  /**
   * Get constituent status - single endpoint that returns everything we need to show.
   */
  public getConstituentStatus(constituentId: string): Observable<DonorAtlasSummary> {
    return this.http.get<DonorAtlasStatusResponse>(
      `${this.apiBaseUrl}/constituents/${constituentId}/status`,
      { headers: this.getHeaders() }
    ).pipe(
      map(({ status, ...summary }) => summary),
      this.logAndRethrow('Error fetching constituent status')
    );
  }

  /**
   * Start sync for a constituent. The backend fetches everything from
   * Raiser's Edge NXT, so the body is just the ID.
   */
  public startSync(constituentId: string): Observable<BaseResponse> {
    return this.http.put<BaseResponse>(
      `${this.apiBaseUrl}/constituents/sync`,
      { constituent_ids: [constituentId] },
      { headers: this.getHeaders() }
    ).pipe(this.logAndRethrow('Error starting sync'));
  }

  /**
   * Resync a constituent that has already been synced.
   */
  public resyncConstituent(constituentId: string): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(
      `${this.apiBaseUrl}/constituents/${constituentId}/resync`,
      {},
      { headers: this.getHeaders() }
    ).pipe(this.logAndRethrow('Error resyncing constituent'));
  }

  /**
   * Type-ahead donor search for the manual-match picker. The same
   * endpoint resolves a pasted DonorAtlas profile URL to one donor.
   */
  public searchDonors(query: string): Observable<DonorSearchResponse> {
    return this.http.get<DonorSearchResponse>(
      `${this.apiBaseUrl}/donors/search`,
      { headers: this.getHeaders(), params: { q: query } }
    ).pipe(this.logAndRethrow('Error searching donors'));
  }

  /**
   * Manually match this constituent to a user-chosen donor and start
   * the write-back job. Charges 1 import.
   */
  public manualMatchConstituent(constituentId: string, donorId: string): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(
      `${this.apiBaseUrl}/constituents/${constituentId}/match`,
      { donor_id: donorId },
      { headers: this.getHeaders() }
    ).pipe(this.logAndRethrow('Error matching constituent'));
  }

  /**
   * Unsync a constituent from DonorAtlas.
   */
  public unsyncConstituent(constituentId: string): Observable<BaseResponse> {
    return this.http.post<BaseResponse>(
      `${this.apiBaseUrl}/constituents/${constituentId}/unsync`,
      {},
      { headers: this.getHeaders() }
    ).pipe(this.logAndRethrow('Error unsyncing constituent'));
  }

  /**
   * Get the embed URL and donor data for displaying the donor profile in an iframe.
   *
   * Parameters
   * ----------
   * constituentId : string
   *     The Raiser's Edge NXT constituent ID.
   *
   * Returns
   * -------
   * Observable<EmbedDataResponse>
   *     The response containing the embed URL and pre-fetched donor data.
   */
  public getEmbedData(constituentId: string): Observable<EmbedDataResponse> {
    return this.http.get<EmbedDataResponse>(
      `${this.apiBaseUrl}/constituents/${constituentId}/embed_data`,
      { headers: this.getHeaders() }
    ).pipe(this.logAndRethrow('Error fetching embed data'));
  }
}
